/**
 * Where an application came from ("source channel"), recorded on applications/{id}.sourceChannel by
 * the applyToJob Cloud Function. Shared by the browser (link tagging, capture, list columns, counts,
 * CSV) and the server (validation, via the generated copy from scripts/syncFunctionsGenerated.mjs).
 *
 * Links carry the existing ?src= pattern (see src/lib/signups/signupSource.ts):
 *   /careers/:orgId job links       ?src=careers    -> careers    "Careers page"
 *   a plain link with no ?src=                      -> direct     "Direct link"
 *   Google's job search (JSON-LD url, or a google referrer when the URL lost its tag)
 *                                   ?src=google     -> google     "Google job search"
 *   Arevina job search / dashboard  ?src=search     -> search     "Arevina job search"
 *   an institute's class link       ?src=institute  -> institute  "Institute class link"
 *   a link an employer sends to someone they invite
 *                                   ?src=invite     -> invite     "Employer invite"
 *   any other tag                                   -> other      "Other"
 *
 * This is separate from the candidate's own answer to "How did you hear about this job?"
 * (applications.applicantSource, free choice, shown as given). Tracking only: nothing ranks or
 * filters applicants on it.
 *
 * Pure: no Firebase, no browser globals.
 */

export const SOURCE_CHANNELS = ['careers', 'direct', 'google', 'search', 'institute', 'invite', 'other'] as const;
export type SourceChannel = (typeof SOURCE_CHANNELS)[number];

export const SOURCE_CHANNEL_LABELS: Record<SourceChannel, string> = {
  careers: 'Careers page',
  direct: 'Direct link',
  google: 'Google job search',
  search: 'Arevina job search',
  institute: 'Institute class link',
  invite: 'Employer invite',
  other: 'Other',
};

/** Short lower-case words for the per-job summary line ("3 careers page, 2 Google, 1 direct"). */
export const SOURCE_CHANNEL_SHORT: Record<SourceChannel, string> = {
  careers: 'careers page',
  direct: 'direct',
  google: 'Google',
  search: 'job search',
  institute: 'institute',
  invite: 'invite',
  other: 'other',
};

/** An application written before source tracking (or by an older page) has no channel. */
export const SOURCE_NOT_RECORDED_LABEL = 'Not recorded';
export const SOURCE_NOT_RECORDED_SHORT = 'not recorded';

/** Accepted spellings of a tag, beyond the channel names themselves. */
const ALIASES: Record<string, SourceChannel> = {
  career: 'careers',
  careers_page: 'careers',
  'careers-page': 'careers',
  careerspage: 'careers',
  google_jobs: 'google',
  'google-jobs': 'google',
  googlejobs: 'google',
  gfj: 'google',
  job_search: 'search',
  'job-search': 'search',
  jobs: 'search',
  arevina: 'search',
  class: 'institute',
  institute_class: 'institute',
  'institute-class': 'institute',
  employer_invite: 'invite',
  'employer-invite': 'invite',
  invited: 'invite',
};

const VALID_TAG = /^[a-z0-9_-]{1,40}$/;

export function isSourceChannel(value: unknown): value is SourceChannel {
  return typeof value === 'string' && (SOURCE_CHANNELS as readonly string[]).includes(value);
}

/**
 * A raw ?src= value (or what a client sent to applyToJob) as a channel.
 *   null / undefined / empty -> 'direct' (a plain link)
 *   a channel name or alias  -> that channel
 *   anything else            -> 'other' (never stores the text a stranger typed into a URL)
 */
export function normalizeSourceChannel(raw: unknown): SourceChannel {
  if (raw === null || raw === undefined) return 'direct';
  if (typeof raw !== 'string') return 'other';
  const v = raw.trim().toLowerCase();
  if (!v) return 'direct';
  if (!VALID_TAG.test(v)) return 'other';
  if (isSourceChannel(v)) return v;
  return ALIASES[v] ?? 'other';
}

/**
 * What applyToJob stores: the normalized channel, or null when the client sent nothing at all (a
 * page loaded before source tracking shipped: that application reads "Not recorded", not "Direct").
 */
export function sourceChannelForApplication(raw: unknown): SourceChannel | null {
  if (raw === undefined) return null;
  return normalizeSourceChannel(raw);
}

/** A referrer that is a Google host (google.com, google.co.uk, www.google.com ...). */
export function isGoogleReferrer(referrer: unknown): boolean {
  if (typeof referrer !== 'string' || !referrer) return false;
  try {
    const host = new URL(referrer).hostname.toLowerCase();
    return /(^|\.)google\.(com|[a-z]{2}|co\.[a-z]{2}|com\.[a-z]{2})$/.test(host);
  } catch {
    return false;
  }
}

/**
 * The channel for a landing on a job page: an explicit ?src= tag wins; with no tag, a Google
 * referrer reads as Google's job search (the indexed URL is the plain canonical one); otherwise null,
 * meaning "no tag on this link" (the caller decides whether that is a direct visit).
 */
export function channelFromLanding(search: string | null | undefined, referrer?: string | null): SourceChannel | null {
  let raw: string | null = null;
  try {
    raw = new URLSearchParams(search || '').get('src');
  } catch {
    raw = null;
  }
  if (raw !== null && raw.trim()) return normalizeSourceChannel(raw);
  if (isGoogleReferrer(referrer)) return 'google';
  return null;
}

/** `path` with `?src=<channel>` added (keeps an existing query string). */
export function withSourceParam(path: string, channel: SourceChannel): string {
  const [base, hash = ''] = path.split('#');
  const sep = base.includes('?') ? '&' : '?';
  return `${base}${sep}src=${channel}${hash ? `#${hash}` : ''}`;
}

/** The stored value on an application as a channel, or null when none was recorded. */
export function storedSourceChannel(app: { sourceChannel?: unknown } | null | undefined): SourceChannel | null {
  const v = app?.sourceChannel;
  return isSourceChannel(v) ? v : null;
}

export function sourceChannelLabel(value: unknown): string {
  return isSourceChannel(value) ? SOURCE_CHANNEL_LABELS[value] : SOURCE_NOT_RECORDED_LABEL;
}

export type SourceCounts = Record<SourceChannel | 'not_recorded', number>;

export function countSourceChannels(apps: ReadonlyArray<{ sourceChannel?: unknown }>): SourceCounts {
  const counts = Object.fromEntries([...SOURCE_CHANNELS, 'not_recorded'].map((k) => [k, 0])) as SourceCounts;
  for (const app of apps) counts[storedSourceChannel(app) ?? 'not_recorded'] += 1;
  return counts;
}

/**
 * "Sources: 3 careers page, 2 Google, 1 direct" (largest first; ties in channel order; "not
 * recorded" always last). Empty string when there are no applications.
 */
export function formatSourceSummary(counts: SourceCounts): string {
  const parts = SOURCE_CHANNELS.map((c, i) => ({ c, i, n: counts[c] || 0 }))
    .filter((x) => x.n > 0)
    .sort((a, b) => b.n - a.n || a.i - b.i)
    .map((x) => `${x.n} ${SOURCE_CHANNEL_SHORT[x.c]}`);
  if ((counts.not_recorded || 0) > 0) parts.push(`${counts.not_recorded} ${SOURCE_NOT_RECORDED_SHORT}`);
  return parts.length ? `Sources: ${parts.join(', ')}` : '';
}
